/**
 * @fileoverview SignIn component for user authentication with Supabase
 * @active
 * @see {@link src/services/auth.service.ts} - Auth service with Supabase integration
 * @see {@link src/utils/authHelpers.ts} - Auth utilities with validation schemas
 * @see {@link src/components/ui/auth/ExtendedAuthInput.tsx} - Input component
 * @see {@link src/components/auth/TwoFAModal.tsx} - Two-factor authentication modal
 * @see {@link src/hooks/useAuth.ts} - Centralized auth hook
 * @see {@link src/types/userAuth.ts} - Auth type definitions
 * @see {@link https://supabase.com/docs/guides/auth/auth-helpers/nextjs} - Supabase Auth Helpers
 */

"use client";

import { useEffect, useState, memo } from "react";

import { Flex, Text, useDisclosure, useToast } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useForm } from "react-hook-form";

import { useUserSignInMutation } from "@/services/auth.service";
import { useAuth } from "@/hooks/useAuth";
import { authToast, authValidation, signInSchema } from "@/utils/authHelpers";
import { useDispatch } from "react-redux";
import { authActions } from "@/store/auth/auth.slice";
import { SignInParams } from "@/types/userAuth";
import { createClientComponent } from "@/app/utils/supabase/client";

import FormLayout from "../../layouts/auth/FormLayout";
import BtnDivider from "../ui/auth/BtnDivider";
import ExtendedAuthInput from "../ui/auth/ExtendedAuthInput";
import FormButton from "../ui/auth/FormButton";
import FormFooter from "../ui/auth/FormFooter";
import FormHeading from "../ui/auth/FormHeading";
import DefaultCheckbox from "../ui/inputs/checkbox/DefaultCheckbox";

import AuthSocialBtn from "./AuthSocialBtn";
import TwoFAModal from "./TwoFAModal";

/**
 * SignIn component for user authentication
 * Uses RTK Query hooks directly for better state management
 *
 * @example
 * <SignIn />
 */
const SignIn = () => {
  // Verify Supabase client is available early to prevent runtime errors
  const [isSupabaseInitialized, setIsSupabaseInitialized] = useState(false);
  const [initError, setInitError] = useState<Error | null>(null);

  // Check if Supabase is properly initialized
  useEffect(() => {
    try {
      // Test Supabase client creation
      const supabase = createClientComponent();
      if (supabase && supabase.auth) {
        setIsSupabaseInitialized(true);
      } else {
        setInitError(
          new Error("Supabase client initialized but auth is not available")
        );
      }
    } catch (error) {
      console.error("Error initializing Supabase client:", error);
      setInitError(
        error instanceof Error
          ? error
          : new Error("Failed to initialize Supabase client")
      );
    }
  }, []);

  const { isOpen, onClose } = useDisclosure();
  const [checked, setChecked] = useState(false);
  const [userData, setUserData] = useState<
    | {
        email: string;
        password: string;
        [key: string]: any;
      }
    | undefined
  >(undefined);

  const [userSignIn, { isLoading, isSuccess, isError, error }] =
    useUserSignInMutation();
  const searchParams = useSearchParams();
  const urlError = searchParams.get("error");
  const router = useRouter();
  const toast = useToast();
  const dispatch = useDispatch();

  // Only use what we need from useAuth
  const { verifyFormData } = useAuth({
    requiredFields: ["email", "password"],
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignInParams>({
    resolver: yupResolver(signInSchema) as any, // Type assertion to bypass the resolver type mismatch
    reValidateMode: "onChange",
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const authData = watch();
  /** Verifies if the form data meets the required criteria */
  const verifyForm = verifyFormData(authData);

  /**
   * Toggles the state of the 'Remember me' checkbox.
   */
  const rememberMeHandler = () => {
    setChecked(!checked);
  };

  /**
   * Handles the form submission event for user sign-in.
   * Clears any previous auth errors and calls the `userSignIn` mutation
   * from the auth service with the provided credentials.
   *
   * @param {SignInParams} data - The validated form data containing email and password.
   */
  const onSubmitHandler = async (data: SignInParams) => {
    // Make sure Supabase is initialized before attempting sign-in
    if (!isSupabaseInitialized) {
      toast(
        authToast.createErrorToast(
          initError ||
            "Authentication service is not available. Please try again later."
        )
      );
      return;
    }

    try {
      dispatch(authActions.setLastError(null));
      await userSignIn({
        email: data.email,
        password: data.password,
        onClose,
      });
    } catch (signInError) {
      console.error("Sign-in error:", signInError);
      toast(authToast.createErrorToast(signInError));
    }
  };

  // Handle successful login
  useEffect(() => {
    if (isSuccess) {
      localStorage.setItem("pageCount", "0");
      toast(authToast.createSuccessToast("Logged in successfully"));
      window.location.href = "/";
    }
  }, [isSuccess, toast]);

  // Handle login errors
  useEffect(() => {
    if (isError && error) {
      toast(authToast.createErrorToast(error));
    }
  }, [isError, error, toast]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("pageCount", "0");
    }
  }, []);

  useEffect(() => {
    if (urlError === "Callback") router.push("/sign-in");
  }, [urlError, router]);

  // Show error if Supabase failed to initialize
  if (initError) {
    return (
      <FormLayout>
        <Flex
          flexDir={"column"}
          w={"100%"}
          justifyContent="center"
          alignItems="center"
        >
          <Text color="red.500" fontWeight="bold" mb={4}>
            Authentication service is currently unavailable
          </Text>
          <Text mb={4}>
            Please try again later or contact support if the issue persists.
          </Text>
          <Link href="/">
            <Text color="blue.500" textDecoration="underline">
              Return to Home Page
            </Text>
          </Link>
        </Flex>
      </FormLayout>
    );
  }

  return (
    <>
      {isOpen && (
        <TwoFAModal
          isOpen={isOpen}
          onClose={onClose}
          userData={userData}
          provider="credentials"
          access_token=""
        />
      )}
      <FormLayout>
        <Flex flexDir={"column"} w={"100%"}>
          <Flex
            as={"form"}
            onSubmit={handleSubmit(onSubmitHandler)}
            flexDir={"column"}
          >
            <FormHeading
              title={"Venture capital for everyone"}
              content={"Login to aVenture to continue"}
              styles={{}}
              wrapStyles={{}}
            />
            <Flex flexDir={"column"} rowGap={"20px"}>
              <ExtendedAuthInput
                control={control}
                errors={errors}
                label="Email"
                input={{ id: "email", type: "email" }}
              />
              <ExtendedAuthInput
                control={control}
                errors={errors}
                label="Password"
                input={{ id: "password", type: "password" }}
              />
            </Flex>
            <Flex align={"center"} justify={"end"}>
              <Link href={"/forgot-password"}>
                <Text
                  mt={"4px"}
                  textAlign={"end"}
                  color={"blue"}
                  fontSize={"14px"}
                  letterSpacing={"0.56px"}
                  fontWeight={"700"}
                  fontFamily={"Roboto"}
                  _hover={{ textDecoration: "underline" }}
                >
                  Forgot your password?
                </Text>
              </Link>
            </Flex>
            <FormButton
              button={{
                type: "submit",
                mt: "18px",
                h: "40px",
                isDisabled: !verifyForm || isLoading || !isSupabaseInitialized,
                isLoading: isLoading,
              }}
              title={"Login"}
            />
            <DefaultCheckbox
              {...{
                text: (
                  <Text fontSize={"14px"} mt={"2.5px"} letterSpacing={"0.56px"}>
                    {"Remember me"}
                  </Text>
                ),
                checked,
                checkHandler: rememberMeHandler,
                checkbox: { mt: "8px" },
              }}
            />
          </Flex>
          <BtnDivider content={"Or login with"} />
          <AuthSocialBtn />
          <FormFooter type={"signIn"} styles={{ mt: "24px" }} />
        </Flex>
      </FormLayout>
    </>
  );
};

export default memo(SignIn);
